import React from 'react';
import * as LabelPrimitive from '@radix-ui/react-label';
import cn from './cn';

/**
 * The three form controls the CMS actually uses, plus its label.
 *
 * `Select` is a **native `<select>`**, not Radix's. That is a deliberate limit on the
 * blast radius: Radix Select takes `<Select.Item>` children and a controlled `value`
 * contract, so adopting it would mean rewriting the `<option>` lists at every call site
 * and changing how each form reads its value. This is a restyle, not a rewrite, so the
 * element stays and only its paint changes. Swapping in Radix later is a change to this
 * file.
 *
 * The shared control styling is one string so an input, a select and a textarea cannot
 * drift apart, which is exactly what happened to the hand-written ones.
 *
 * Redesign iteration 1: these are the same field as the MUI one the theme draws (see
 * `store/rootComponents/Theme.js`), because a dialog built from these sits next to a form
 * built from MUI and the two have to read as one control. 40px, a 1px --input-border
 * edge, --muted under the pointer, --brand with a 3px halo on focus, --danger when
 * invalid, and --fill with a --border-light edge when disabled. No shadow: the edge is
 * what makes it a control now that it clears 3:1.
 */
const control = [
  'tw-block tw-w-full tw-font-sans tw-text-body tw-text-ink',
  'tw-bg-white tw-rounded-control',
  'tw-border tw-border-solid tw-border-input-border',
  'tw-h-input tw-px-3 tw-py-0',
  'tw-transition-colors tw-duration-150',
  'placeholder:tw-text-placeholder-ink',
  // Tailwind orders hover before focus and disabled, and the aria states after all
  // three, so focus, a disabled field and an invalid one each win over the pointer.
  'hover:tw-border-muted',
  'focus:tw-outline-none focus:tw-border-brand focus:tw-ring focus:tw-ring-ring-brand',
  'disabled:tw-bg-fill disabled:tw-border-line-light disabled:tw-text-muted',
  'disabled:tw-cursor-not-allowed',
  'aria-[invalid=true]:tw-border-danger aria-[invalid=true]:focus:tw-ring-ring-danger',
];

export const controlClasses = (className) => cn(control, className);

/**
 * The label above a field: 13/600 --ink-2, 6px over the box. The MUI theme draws the same
 * label (`staticLabel` in Theme.js), and `formFields.test.js` holds the two together.
 */
export const fieldLabelClasses = cn(
  'tw-block tw-m-0 tw-mb-label tw-font-sans tw-text-small tw-font-semibold tw-text-ink-2'
);

/**
 * Help text: 12px --muted. It sits 6px under its field; that gap is `fieldSlotClasses`,
 * kept apart because MUI's helper text already spaces itself from the box.
 */
export const fieldHelpClasses = cn('tw-block tw-text-caption tw-text-muted');

/** The error, in the help text's slot: 12/600 --error-text, after a 14px circle-alert. */
export const fieldErrorClasses = cn(
  'tw-flex tw-items-start tw-gap-1.5 tw-text-caption tw-font-semibold tw-text-error-text'
);

/** The 6px between a field's box and the help or error slot under it. */
export const fieldSlotClasses = 'tw-mt-label';

export const Input = React.forwardRef(({ className, type = 'text', ...props }, ref) => (
  <input ref={ref} type={type} className={controlClasses(className)} {...props} />
));
Input.displayName = 'Input';

/** A textarea is the same control at 80px and up, and it grows downward only. */
export const Textarea = React.forwardRef(({ className, ...props }, ref) => (
  <textarea
    ref={ref}
    className={controlClasses(['tw-h-auto tw-min-h-textarea tw-py-2.5 tw-resize-y', className])}
    {...props}
  />
));
Textarea.displayName = 'Textarea';

/**
 * The chevron is a background image rather than an icon element, because a native
 * `<select>` cannot have children other than options.
 */
export const Select = React.forwardRef(({ className, ...props }, ref) => (
  <select
    ref={ref}
    className={controlClasses([
      'tw-appearance-none tw-pr-8 tw-cursor-pointer',
      // `position:` labels the arbitrary value; unlabelled, tailwind-merge reads it as a
      // background colour and drops the chevron and the white ground before it.
      'tw-bg-select-chevron tw-bg-[length:16px] tw-bg-[position:right_0.6rem_center] tw-bg-no-repeat',
      className,
    ])}
    {...props}
  />
));
Select.displayName = 'Select';

export const Label = React.forwardRef(({ className, ...props }, ref) => (
  <LabelPrimitive.Root
    ref={ref}
    className={cn(
      fieldLabelClasses,
      'tw-select-none',
      // A label for a disabled control should not look clickable. No opacity: a disabled
      // field says so in its box, and an opacity of a token is a new colour.
      'peer-disabled:tw-cursor-not-allowed',
      className
    )}
    {...props}
  />
));
Label.displayName = 'Label';

export default Input;
